import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import {
    ArrowLeftIcon,
    WalletIcon,
    UserCircleIcon,
    EnvelopeIcon,
    PhoneIcon,
    CalendarIcon,
    ClockIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    PlusCircleIcon,
    MinusCircleIcon,
    BanknotesIcon,
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon,
    ArrowPathIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import { usePermissions } from "@/features/auth/usePermissions";
import { getWalletDetails, creditWallet, debitWallet } from "../api/walletPaymentsApi";
import { extractApiError } from "@/lib/api-error";

// ============================================================================
// Badges
// ============================================================================

const StatusBadge = ({ status }: { status: string }) => {
    const s = status ?? "UNKNOWN";
    const styles: Record<string, string> = {
        ACTIVE: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        FROZEN: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        CLOSED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    };
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium ${styles[s] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            <span className={`h-2 w-2 rounded-full ${s === "ACTIVE" ? "bg-emerald-500" : s === "FROZEN" ? "bg-blue-500" : s === "CLOSED" ? "bg-rose-500" : "bg-slate-400"}`} />
            {s}
        </span>
    );
};

const DirectionBadge = ({ direction }: { direction: string }) => {
    return direction === "CREDIT" ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
            <ArrowUpIcon className="h-3 w-3" /> CREDIT
        </span>
    ) : (
        <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700 dark:bg-rose-900/30 dark:text-rose-400">
            <ArrowDownIcon className="h-3 w-3" /> DEBIT
        </span>
    );
};

const TxStatusBadge = ({ status }: { status: string }) => {
    const s = status ?? "UNKNOWN";
    const styles: Record<string, string> = {
        COMPLETED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        FAILED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        REVERSED: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    };
    return (
        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${styles[s] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            {s}
        </span>
    );
};

// ============================================================================
// Helpers
// ============================================================================

const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return "—";
    try {
        return new Intl.DateTimeFormat("en-NG", {
            month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
        }).format(new Date(dateStr));
    } catch {
        return dateStr;
    }
};

const formatCurrency = (amount: number | null | undefined, currency = "NGN") => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("en-NG", { style: "currency", currency, maximumFractionDigits: 2 }).format(amount);
};

// ============================================================================
// Modal Component
// ============================================================================

type ModalProps = {
    open: boolean;
    title: string;
    onClose: () => void;
    children: React.ReactNode;
};

const Modal = ({ open, title, onClose, children }: ModalProps) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={onClose} />
            <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-900">
                <h3 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">{title}</h3>
                {children}
            </div>
        </div>
    );
};

// ============================================================================
// Component
// ============================================================================

export const WalletDetailsRoute = () => {
    const { walletId } = useParams<{ walletId: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { hasPermission } = usePermissions();

    // Modal states
    const [creditModal, setCreditModal] = useState(false);
    const [debitModal, setDebitModal] = useState(false);
    const [amount, setAmount] = useState("");
    const [reason, setReason] = useState("");
    const [forceNegative, setForceNegative] = useState(false);
    const [actionError, setActionError] = useState("");

    const { data, isLoading, error } = useQuery({
        queryKey: ["wallet-details", walletId],
        queryFn: () => getWalletDetails(walletId!),
        enabled: !!walletId,
    });

    const wallet = data?.data;
    const canAdjustWallet = hasPermission(SECTION_PERMISSIONS.WALLET_ADJUST[0]);

    const creditMutation = useMutation({
        mutationFn: () => creditWallet(walletId!, { amount: parseFloat(amount), reason }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["wallet-details", walletId] });
            queryClient.invalidateQueries({ queryKey: ["wallets"] });
            setCreditModal(false);
            resetForm();
        },
        onError: (err) => setActionError(extractApiError(err)),
    });

    const debitMutation = useMutation({
        mutationFn: () => debitWallet(walletId!, { amount: parseFloat(amount), reason, forceNegative }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["wallet-details", walletId] });
            queryClient.invalidateQueries({ queryKey: ["wallets"] });
            setDebitModal(false);
            resetForm();
        },
        onError: (err) => setActionError(extractApiError(err)),
    });

    const resetForm = () => {
        setAmount("");
        setReason("");
        setForceNegative(false);
        setActionError("");
    };

    // ── Loading / Error ──────────────────────────────────────────────────────
    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
            </div>
        );
    }
    if (error || !wallet) {
        return (
            <div className="py-20 text-center">
                <p className="text-rose-500">{error ? String(error) : "Wallet not found"}</p>
                <Button variant="secondary" size="sm" className="mt-4" onClick={() => navigate(-1)}>Go Back</Button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Back Button */}
            <button
                onClick={() => navigate("/wallet-payments/wallets")}
                className="flex items-center gap-2 text-sm font-medium text-slate-500 transition-colors hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
            >
                <ArrowLeftIcon className="h-4 w-4" /> Back to Wallets
            </button>

            {/* Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg shadow-primary-500/25">
                        <WalletIcon className="h-7 w-7 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                            {wallet.user.firstName} {wallet.user.lastName}'s Wallet
                        </h1>
                        <div className="mt-1 flex items-center gap-3">
                            <StatusBadge status={wallet.status} />
                            <span className="text-sm text-slate-500 dark:text-slate-400">{wallet.user.role}</span>
                        </div>
                    </div>
                </div>
                {canAdjustWallet ? (
                    <div className="flex flex-wrap gap-2">
                        <Button variant="secondary" size="sm" onClick={() => { resetForm(); setCreditModal(true); }}>
                            <PlusCircleIcon className="mr-1.5 h-4 w-4 text-emerald-500" /> Credit
                        </Button>
                        <Button variant="secondary" size="sm" onClick={() => { resetForm(); setDebitModal(true); }}>
                            <MinusCircleIcon className="mr-1.5 h-4 w-4 text-rose-500" /> Debit
                        </Button>
                    </div>
                ) : null}
            </div>

            {/* Balance Card */}
            <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6 dark:border-slate-800 dark:from-slate-900 dark:to-slate-800/50">
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Current Balance</p>
                <p className="mt-1 text-4xl font-bold text-slate-900 dark:text-white">
                    {formatCurrency(wallet.balance, wallet.currency)}
                </p>
                <p className="mt-1 text-xs text-slate-400">{wallet.currency}</p>
            </div>

            {/* User Info + Stats Grid */}
            <div className="grid gap-6 lg:grid-cols-2">
                {/* User Info */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">User Information</h2>
                    <div className="space-y-3">
                        <div className="flex items-center gap-3 text-sm">
                            <UserCircleIcon className="h-5 w-5 text-slate-400" />
                            <span className="text-slate-500 dark:text-slate-400">Name:</span>
                            <span className="font-medium text-slate-900 dark:text-white">{wallet.user.firstName} {wallet.user.lastName}</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                            <EnvelopeIcon className="h-5 w-5 text-slate-400" />
                            <span className="text-slate-500 dark:text-slate-400">Email:</span>
                            <span className="font-medium text-slate-900 dark:text-white">{wallet.user.email}</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                            <PhoneIcon className="h-5 w-5 text-slate-400" />
                            <span className="text-slate-500 dark:text-slate-400">Phone:</span>
                            <span className="font-medium text-slate-900 dark:text-white">{wallet.user.phoneNumber || "—"}</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                            <CalendarIcon className="h-5 w-5 text-slate-400" />
                            <span className="text-slate-500 dark:text-slate-400">Created:</span>
                            <span className="font-medium text-slate-900 dark:text-white">{formatDate(wallet.createdAt)}</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                            <ClockIcon className="h-5 w-5 text-slate-400" />
                            <span className="text-slate-500 dark:text-slate-400">Last Transaction:</span>
                            <span className="font-medium text-slate-900 dark:text-white">{formatDate(wallet.lastTransactionAt)}</span>
                        </div>
                    </div>
                </div>

                {/* Stats */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Activity Summary</h2>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="rounded-xl bg-emerald-50 p-4 dark:bg-emerald-900/20">
                            <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                                <ArrowTrendingUpIcon className="h-4 w-4" />
                                <span className="text-xs font-medium">Credits (7d)</span>
                            </div>
                            <p className="mt-1 text-lg font-bold text-emerald-700 dark:text-emerald-300">
                                {formatCurrency(wallet.stats?.totalCredits7Days, wallet.currency)}
                            </p>
                        </div>
                        <div className="rounded-xl bg-rose-50 p-4 dark:bg-rose-900/20">
                            <div className="flex items-center gap-2 text-rose-600 dark:text-rose-400">
                                <ArrowTrendingDownIcon className="h-4 w-4" />
                                <span className="text-xs font-medium">Debits (7d)</span>
                            </div>
                            <p className="mt-1 text-lg font-bold text-rose-700 dark:text-rose-300">
                                {formatCurrency(wallet.stats?.totalDebits7Days, wallet.currency)}
                            </p>
                        </div>
                        <div className="rounded-xl bg-emerald-50 p-4 dark:bg-emerald-900/20">
                            <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                                <ArrowTrendingUpIcon className="h-4 w-4" />
                                <span className="text-xs font-medium">Credits (30d)</span>
                            </div>
                            <p className="mt-1 text-lg font-bold text-emerald-700 dark:text-emerald-300">
                                {formatCurrency(wallet.stats?.totalCredits30Days, wallet.currency)}
                            </p>
                        </div>
                        <div className="rounded-xl bg-rose-50 p-4 dark:bg-rose-900/20">
                            <div className="flex items-center gap-2 text-rose-600 dark:text-rose-400">
                                <ArrowTrendingDownIcon className="h-4 w-4" />
                                <span className="text-xs font-medium">Debits (30d)</span>
                            </div>
                            <p className="mt-1 text-lg font-bold text-rose-700 dark:text-rose-300">
                                {formatCurrency(wallet.stats?.totalDebits30Days, wallet.currency)}
                            </p>
                        </div>
                        <div className="col-span-2 rounded-xl bg-slate-50 p-4 dark:bg-slate-800">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Transactions (7d)</p>
                                    <p className="text-lg font-bold text-slate-900 dark:text-white">{wallet.stats?.transactionCount7Days ?? 0}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Transactions (30d)</p>
                                    <p className="text-lg font-bold text-slate-900 dark:text-white">{wallet.stats?.transactionCount30Days ?? 0}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent Transactions */}
            <div className="rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                <div className="border-b border-slate-100 px-6 py-4 dark:border-slate-800">
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Recent Transactions</h2>
                </div>
                {(!wallet.recentTransactions || wallet.recentTransactions.length === 0) ? (
                    <div className="py-12 text-center text-sm text-slate-500 dark:text-slate-400">No transactions yet.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-100 bg-slate-50/50 dark:border-slate-800 dark:bg-slate-800/50">
                                    <th className="px-4 py-3 text-left font-medium text-slate-500 dark:text-slate-400">Type</th>
                                    <th className="px-4 py-3 text-left font-medium text-slate-500 dark:text-slate-400">Direction</th>
                                    <th className="px-4 py-3 text-right font-medium text-slate-500 dark:text-slate-400">Amount</th>
                                    <th className="px-4 py-3 text-left font-medium text-slate-500 dark:text-slate-400">Description</th>
                                    <th className="px-4 py-3 text-center font-medium text-slate-500 dark:text-slate-400">Status</th>
                                    <th className="px-4 py-3 text-left font-medium text-slate-500 dark:text-slate-400">Date</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {wallet.recentTransactions.map((tx) => (
                                    <tr
                                        key={tx.transactionId}
                                        onClick={() => navigate(`/wallet-payments/transactions/${tx.transactionId}`)}
                                        className="cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                    >
                                        <td className="px-4 py-3">
                                            <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                                {tx.type?.replace(/_/g, " ") ?? "—"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <DirectionBadge direction={tx.direction} />
                                        </td>
                                        <td className={`px-4 py-3 text-right font-semibold ${tx.direction === "CREDIT" ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
                                            {tx.direction === "CREDIT" ? "+" : "−"}{formatCurrency(tx.amount, tx.currency)}
                                        </td>
                                        <td className="max-w-[200px] truncate px-4 py-3 text-slate-500 dark:text-slate-400">
                                            {tx.description || "—"}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <TxStatusBadge status={tx.status} />
                                        </td>
                                        <td className="px-4 py-3 text-slate-500 dark:text-slate-400">
                                            {formatDate(tx.processedAt || tx.createdAt)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ── Credit Modal ── */}
            <Modal open={creditModal} title="Credit Wallet" onClose={() => setCreditModal(false)}>
                <div className="space-y-4">
                    {actionError && <p className="text-sm text-rose-500">{actionError}</p>}
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Amount ({wallet.currency})</label>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                            placeholder="e.g. 5000"
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Reason</label>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={3}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                            placeholder="Reason for crediting this wallet…"
                        />
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button variant="secondary" size="sm" onClick={() => setCreditModal(false)}>Cancel</Button>
                        <Button
                            size="sm"
                            onClick={() => creditMutation.mutate()}
                            disabled={!amount || !reason || creditMutation.isPending}
                        >
                            {creditMutation.isPending ? "Processing…" : "Credit Wallet"}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* ── Debit Modal ── */}
            <Modal open={debitModal} title="Debit Wallet" onClose={() => setDebitModal(false)}>
                <div className="space-y-4">
                    {actionError && <p className="text-sm text-rose-500">{actionError}</p>}
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Amount ({wallet.currency})</label>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                            placeholder="e.g. 2000"
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Reason</label>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={3}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                            placeholder="Reason for debiting this wallet…"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            id="forceNegative"
                            type="checkbox"
                            checked={forceNegative}
                            onChange={(e) => setForceNegative(e.target.checked)}
                            className="rounded border-slate-300 dark:border-slate-600"
                        />
                        <label htmlFor="forceNegative" className="text-sm text-slate-600 dark:text-slate-400">
                            Allow balance to go negative
                        </label>
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button variant="secondary" size="sm" onClick={() => setDebitModal(false)}>Cancel</Button>
                        <Button
                            size="sm"
                            onClick={() => debitMutation.mutate()}
                            disabled={!amount || !reason || debitMutation.isPending}
                        >
                            {debitMutation.isPending ? "Processing…" : "Debit Wallet"}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
